import React, { useState } from 'react';
import JSZip from 'jszip';
import toast, { Toaster } from 'react-hot-toast';
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import {
  CloudUpload,
  CheckCircle,
  Cancel,
  Visibility,
  ArrowBack,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { uploadFolder as uploadFolderApi, getApplicationBlobInfo, API_BASE } from "../api";
import { logout, getUser } from '../services/authService';
import Sidebar from './Sidebar';

// Required files for Super Admin validation (Role ID 2)
const REQUIRED_FILES = [
  'COM_LOAN_Application.pdf',
  'PR_AADHAR.pdf',
  'PR_PAN.pdf',
  'PR_2022_ITR.pdf',
  'PR_2023_ITR.pdf',
  'PR_NETWORTH.pdf',
  'PR_CIBIL.pdf',
  'CO1_AADHAR.pdf',
  'CO1_PAN.pdf',
  'CO1_2022_ITR.pdf',
  'CO1_2023_ITR.pdf',
  'CO1_CIBIL.pdf',
  '2022_BS.pdf',
  '2023_BS.pdf',
  '2024_BS.pdf',
  'COM_CIBIL.pdf',
  'COM_GST_Certificate.pdf',
  '2022_PL.pdf',
  '2023_PL.pdf',
  '2024_PL.pdf',
  'COM_UDYAM_Certificate.pdf',
  'COM_BANK_Summary.xlsx'
];

const UploadDocuments = () => {
  const navigate = useNavigate();
  const [uploadedFiles, setUploadedFiles] = useState({});
  const [isUploading, setIsUploading] = useState(false);
  const [showCompletionUI, setShowCompletionUI] = useState(false);
  const [completionProgress, setCompletionProgress] = useState(0);
  const [showSuccessUI, setShowSuccessUI] = useState(false);
  const [uploadedAppId, setUploadedAppId] = useState(null);
  const [uploadedBaseUrl, setUploadedBaseUrl] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);
  const [openPreview, setOpenPreview] = useState(false);
  
  // Role-based validation states
  const [showValidationDialog, setShowValidationDialog] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [userRole, setUserRole] = useState('Tenant Admin'); // Default role
  
  // Sidebar state
  const [collapsed, setCollapsed] = useState(true);

  // Handle logout
  const handleLogout = async () => {
    console.log(' [UPLOAD] Logout initiated from sidebar');
    try {
      await logout('manual');
      // logout function handles redirect
    } catch (error) {
      console.error(' [UPLOAD] Logout error:', error);
      // Force logout even if API fails
      localStorage.clear();
      navigate('/login');
    }
  };

  // Expected files list
 // Expected files list
const expectedFiles = [
  'Loan Application.pdf',
  'Primary Applicant – Aadhaar Card.pdf',
  'Primary Applicant – PAN Card.pdf',
  'Primary Applicant – Income Tax Return (AY 2022–23).pdf',
  'Primary Applicant – Income Tax Return (AY 2023–24).pdf',
  'Primary Applicant – Net Worth Statement.pdf',
  'Primary Applicant – Credit Report (CIBIL).pdf',
  'Co-Applicant 1 – Aadhaar Card.pdf',
  'Co-Applicant 1 – PAN Card.pdf',
  'Co-Applicant 1 – Income Tax Return (AY 2022–23).pdf',
  'Co-Applicant 1 – Income Tax Return (AY 2023–24).pdf',
  'Co-Applicant 1 – Credit Report (CIBIL).pdf',
  'Company – Balance Sheet (FY 2021–22).pdf',
  'Company – Balance Sheet (FY 2022–23).pdf',
  'Company – Balance Sheet (FY 2023–24).pdf',
  'Company – Credit Report (CIBIL).pdf',
  'Company – GST Registration Certificate.pdf',
  'Company – Profit & Loss Statement (FY 2021–22).pdf',
  'Company – Profit & Loss Statement (FY 2022–23).pdf',
  'Company – Profit & Loss Statement (FY 2023–24).pdf',
  'Company – Udyam Registration Certificate.pdf',
  'Company – Bank Statement Summary.xlsx',
];

  // Handle individual file upload
  const handleFileUpload = (fileName, event) => {
    const file = event.target.files[0];
    if (!file) {
      console.log(' No file selected for:', fileName);
      return;
    }

    console.log(' File selected for', fileName + ':', {
      originalName: file.name,
      size: `${(file.size / 1024 / 1024).toFixed(2)} MB`,
      type: file.type,
      lastModified: new Date(file.lastModified).toLocaleString()
    });

    // Check file type
    const fileExtension = file.name.toLowerCase().split('.').pop();
    console.log(' File extension check:', fileExtension);
    
    if (!['pdf', 'xlsx', 'xls'].includes(fileExtension)) {
      console.error(' Invalid file type:', fileExtension, 'for file:', file.name);
      // alert('Please upload only PDF or Excel files');
      return;
    }

    console.log(' File type validation passed for:', fileName);

    // Store file with metadata
    const fileMetadata = {
      file: file,
      name: file.name,
      size: file.size,
      uploadedAt: new Date(),
      status: 'uploaded'
    };
    
    console.log(' Storing file metadata:', fileMetadata);
    
    setUploadedFiles(prev => {
      const updated = {
        ...prev,
        [fileName]: fileMetadata
      };
      console.log(' Updated files count:', Object.keys(updated).length, '/', expectedFiles.length);
      return updated;
    });
  };

  // Handle file preview
  const handlePreview = (fileName) => {
    const fileData = uploadedFiles[fileName];
    if (!fileData) return;

    const fileURL = URL.createObjectURL(fileData.file);
    setPreviewFile({ url: fileURL, name: fileName });
    setOpenPreview(true);
  };

  // Handle form submission
  const handleSubmit = async () => {
    console.log(' Starting application submission process...');
    
    // Check if at least one file is uploaded
    const uploadedFileCount = Object.keys(uploadedFiles).length;
    console.log(' Uploaded files count:', uploadedFileCount);
    
    if (uploadedFileCount === 0) {
      console.error(' No files uploaded');
      toast.error('Please upload at least one file before submitting.', {
        duration: 4000,
        position: 'top-center',
        style: {
          background: '#f87171',
          color: 'white',
          fontWeight: '500',
          borderRadius: '8px',
          padding: '12px 16px'
        }
      });
      return;
    }

    // Role-based validation for Super Admin (Role ID 2)
    const user = getUser();
    const userRoleId = user?.roleId || 1; // Default to Tenant Admin
    console.log(' User Role ID:', userRoleId);
    
    if (userRoleId === 2) { // Super Admin - strict validation
      console.log(' Applying Super Admin submission validation...');
      
      // Get uploaded file names
      const uploadedFileNames = Object.keys(uploadedFiles);
      console.log(' Super Admin - Uploaded files:', uploadedFileNames);
      console.log(' Super Admin - Required files count:', REQUIRED_FILES.length);
      
      // Check for missing files
      const missingFiles = REQUIRED_FILES.filter(requiredFile => 
        !uploadedFileNames.some(uploadedFile => 
          uploadedFile.toLowerCase() === requiredFile.toLowerCase()
        )
      );
      
      if (missingFiles.length > 0) {
        console.error(' Super Admin validation failed - Missing files:', missingFiles);
        toast.error(
          <div>
            <div style={{ fontWeight: '600', marginBottom: '8px' }}>
              Please upload all required files to proceed
            </div>
            <div style={{ fontSize: '13px', opacity: '0.9' }}>
              Missing {missingFiles.length} of {REQUIRED_FILES.length} required files
            </div>
          </div>,
          {
            duration: 6000,
            position: 'top-center',
            style: {
              background: '#dc2626',
              color: 'white',
              borderRadius: '12px',
              padding: '16px 20px',
              maxWidth: '400px',
              boxShadow: '0 10px 25px rgba(220, 38, 38, 0.3)'
            }
          }
        );
        return;
      }
      
      // Check total count
      if (uploadedFileNames.length !== REQUIRED_FILES.length) {
        console.error(' Super Admin validation failed - File count mismatch');
        toast.error(
          <div>
            <div style={{ fontWeight: '600', marginBottom: '8px' }}>
              Please upload exactly {REQUIRED_FILES.length} required files to proceed
            </div>
            <div style={{ fontSize: '13px', opacity: '0.9' }}>
              Currently uploaded: {uploadedFileNames.length} files
            </div>
          </div>,
          {
            duration: 5000,
            position: 'top-center',
            style: {
              background: '#dc2626',
              color: 'white',
              borderRadius: '12px',
              padding: '16px 20px',
              maxWidth: '400px',
              boxShadow: '0 10px 25px rgba(220, 38, 38, 0.3)'
            }
          }
        );
        return;
      }
      
      console.log(' Super Admin validation passed - All required files present');
      toast.success('All required files validated successfully!', {
        duration: 3000,
        position: 'top-center',
        style: {
          background: '#059669',
          color: 'white',
          fontWeight: '500',
          borderRadius: '8px',
          padding: '12px 16px'
        }
      });
    } else {
      console.log(' Tenant Admin - No additional validation required');
    }
    
    console.log(` Proceeding with ${uploadedFileCount} uploaded files`);
    setIsUploading(true);
    setShowCompletionUI(true);

    try {
      // Create FormData
      const formData = new FormData();
      const timestamp = new Date().getTime();
      const folderName = `application_${timestamp}`;
      console.log(' Generated folder name:', folderName);
      
      formData.append('folderName', folderName);

      // Create a ZIP of the uploaded files and append the ZIP to FormData
      console.log(' Creating ZIP file from uploaded documents...');
      const zip = new JSZip();
      Object.entries(uploadedFiles).forEach(([fileName, fileData]) => {
        console.log(` Adding to ZIP: ${fileName} (${(fileData.size / 1024).toFixed(2)} KB)`);
        // use original filename inside the zip
        zip.file(fileName, fileData.file);
      });

      console.log(' Generating ZIP blob...');
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      console.log(' ZIP created successfully:', `${(zipBlob.size / 1024 / 1024).toFixed(2)} MB`);
      
      // Name the zip using the generated folderName so backend can use it
      formData.append('files', zipBlob, `${folderName}.zip`);

      // Simulate progress
      console.log(' Starting upload progress simulation...');
      let progress = 0;
      const progressInterval = setInterval(() => {
        progress += 5;
        setCompletionProgress(progress);
        if (progress >= 90) {
          console.log('Progress simulation reached 90%, switching to real progress');
          clearInterval(progressInterval);
        }
      }, 200);

      // Upload to backend with real progress using XHR
      const uploadWithProgress = (url, data) =>
        new Promise((resolve, reject) => {
          console.log(' Initiating XHR upload to:', url);
          const xhr = new XMLHttpRequest();
          xhr.open('POST', url);
          
          // Add authentication header
          const accessToken = localStorage.getItem('accessToken');
          if (accessToken) {
            xhr.setRequestHeader('Authorization', `Bearer ${accessToken}`);
            console.log(' Added Authorization header');
          } else {
            console.warn(' No access token found - upload may fail');
          }

          xhr.onload = () => {
            console.log(' Upload completed with status:', xhr.status);
            console.log(' Response text:', xhr.responseText);
            try {
              const res = JSON.parse(xhr.responseText || '{}');
              console.log(' Response data:', res);
              if (xhr.status >= 200 && xhr.status < 300) {
                console.log(' Upload successful');
                resolve(res);
              } else {
                console.error(' Upload failed with status:', xhr.status);
                console.error(' Error response:', res);
                
                // Provide more specific error messages
                let errorMessage = 'Upload failed: Unknown error';
                if (xhr.status === 401) {
                  errorMessage = 'Upload failed: Authentication required. Please login again.';
                } else if (xhr.status === 403) {
                  errorMessage = 'Upload failed: Access denied. Please check your permissions.';
                } else if (xhr.status === 413) {
                  errorMessage = 'Upload failed: File too large.';
                } else if (xhr.status === 400) {
                  errorMessage = res.error || res.message || 'Upload failed: Bad request';
                } else if (xhr.status >= 500) {
                  errorMessage = 'Upload failed: Server error. Please try again.';
                } else if (res.error) {
                  errorMessage = `Upload failed: ${res.error}`;
                } else if (res.message) {
                  errorMessage = `Upload failed: ${res.message}`;
                }
                
                reject({ status: xhr.status, body: res, message: errorMessage });
              }
            } catch (e) {
              console.error(' Error parsing response:', e);
              console.error(' Raw response text:', xhr.responseText);
              reject({ message: 'Upload failed: Invalid server response', error: e });
            }
          };
          xhr.onerror = () => {
            console.error(' Network error during upload');
            reject(new Error('Network error'));
          };
          xhr.upload.onprogress = (e) => {
            if (e.lengthComputable) {
              const percent = Math.round((e.loaded / e.total) * 100);
              console.log(` Upload progress: ${percent}% (${(e.loaded / 1024 / 1024).toFixed(2)}MB / ${(e.total / 1024 / 1024).toFixed(2)}MB)`);
              setCompletionProgress(percent);
            }
          };
          console.log(' Sending upload request...');
          xhr.send(data);
        });

      const url = `${API_BASE}/upload-folder`;
      console.log(' Upload URL:', url);
      const response = await uploadWithProgress(url, formData);

      clearInterval(progressInterval);
      setCompletionProgress(100);

      // Validation removed - backend now accepts any number of files
      console.log(' Validation skipped - proceeding with upload');

      if (!response.success) {
        throw new Error(response.message || 'Upload failed');
      }

      // Fetch application info from Azure Blob immediately (from Home.js logic)
      if (response.applicationData) {
        const applicationId = response.applicationData.applicationId;
        console.log(' Upload successful! Application created with ID:', applicationId);
        console.log(' Application data received:', response.applicationData);
        console.log(' Fetching application-info.json from Azure Blob for:', applicationId);
        
        try {
          // Call new API to get application-info.json from Azure Blob
          const blobInfoResponse = await getApplicationBlobInfo(applicationId);
          console.log(' Blob API response:', blobInfoResponse);
          
          if (blobInfoResponse.success && blobInfoResponse.data) {
            console.log(' application-info.json fetched successfully from Azure Blob:');
            console.log(' Application info data:', JSON.stringify(blobInfoResponse.data, null, 2));
            console.log(' Company name:', blobInfoResponse.data.companyName || 'N/A');
            console.log(' Borrower name:', blobInfoResponse.data.borrowerName || 'N/A');
            console.log(' Created date:', blobInfoResponse.data.createdDate || 'N/A');
            console.log(' Status:', blobInfoResponse.data.status || 'N/A');
            
            // Save app details for success UI
            setUploadedAppId(applicationId);
            setUploadedBaseUrl(response.baseUrl || response.metadataUrl || null);
            
            console.log(' New entry will be added to Home page with ID:', applicationId);
            console.log(' Base URL for files:', response.baseUrl || response.metadataUrl || 'N/A');
          } else {
            console.warn(' Blob info response not successful:', blobInfoResponse);
          }
        } catch (blobError) {
          console.error(' Failed to fetch application-info.json from Azure Blob:', blobError);
          console.warn(' Using fallback response data instead');
          
          // Fallback: Use response data if blob fetch fails
          const applicationId = response.applicationData.applicationId;
          setUploadedAppId(applicationId);
          setUploadedBaseUrl(response.baseUrl || response.metadataUrl || null);
          console.log(' Fallback: Using application ID:', applicationId);
        }
      } else {
        console.warn(' No application data in response:', response);
      }

      // Show success UI
      console.log(' Upload process completed successfully!');
      setTimeout(() => {
        console.log(' Showing success UI');
        setShowCompletionUI(false);
        setShowSuccessUI(true);
      }, 1000);

      // Navigate back after 3 seconds and trigger refresh
      // setTimeout(() => {
      //   console.log('Navigating back to home page...');
      //   console.log('Setting HOME_REFRESH flag for data refresh');
      //   localStorage.setItem('HOME_REFRESH', Date.now().toString());
      //   navigate('/');
      // }, 3000);

    } catch (error) {
      console.error(' Upload process failed:', error);
      console.log(' Error details:', {
        message: error.message,
        status: error.status,
        body: error.body
      });
      
      // Check for role-based validation errors
      if (error && error.body && error.body.validationErrors && error.body.strictValidation) {
        console.error(' Super Admin validation failed:', error.body.validationErrors);
        setValidationErrors(error.body.validationErrors);
        setUserRole(error.body.userRole || 'Super Admin');
        setShowValidationDialog(true);
        setIsUploading(false);
        setShowCompletionUI(false);
        return;
      }
      
      if (error && error.body && error.body.error) {
        console.error(' Server error:', error.body.error);
        toast.error(error.message || `Upload failed: ${error.body.error}`, {
          duration: 5000,
          position: 'top-center',
          style: {
            background: '#dc2626',
            color: 'white',
            fontWeight: '500',
            borderRadius: '8px',
            padding: '12px 16px'
          }
        });
      } else {
        console.error(' Unknown error:', error.message || 'Unknown error');
        toast.error(error.message || `Upload failed: ${error.message || 'Unknown error'}`, {
          duration: 5000,
          position: 'top-center',
          style: {
            background: '#dc2626',
            color: 'white',
            fontWeight: '500',
            borderRadius: '8px',
            padding: '12px 16px'
          }
        });
      }
      
      console.log(' Resetting upload states');
      setIsUploading(false);
      setShowCompletionUI(false);
    }
  };

  // Get status for a file
  const getFileStatus = (fileName) => {
    return uploadedFiles[fileName] ? 'uploaded' : 'pending';
  };

  return (
    <Box sx={{ display: 'flex', bgcolor: '#ffffffff', minHeight: '100vh', fontFamily: '"Nunito Sans", "Roboto", "Arial", sans-serif' }}>
      {/* Sidebar */}
      <Sidebar 
        activeItem="home" 
        collapsed={collapsed} 
        onToggleSidebar={() => setCollapsed(!collapsed)}
        onLogout={handleLogout}
      />
      
      {/* Main Content */}
      <Box sx={{ 
        flex: 1,
        minWidth: 0,
        ml: collapsed ? '4.375rem' : 0,
        transition: 'margin-left 0.3s ease-in-out',
        p: 1,
        pl: 1  // Minimal left padding
      }}>
        {/* Header */}
        <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate('/dashboard')} sx={{ color: '#034A7F' }}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h5" sx={{ color: '#034A7F', fontWeight: 600 }}>
            Upload Application Documents
          </Typography>
        </Box>

        {/* Instructions */}
        <Paper sx={{ p: 1, mb: 1, borderRadius: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Only PDF and Excel files are accepted.
          </Typography>
        </Paper>

        {/* Documents Table */}
        <TableContainer component={Paper} sx={{ borderRadius: 2, mb: 1 }}>
        <Table size="small" sx={{ '& .MuiTableCell-root': { borderRight: 'none' } }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#034A7F' }}>
              <TableCell sx={{ color: 'white', fontWeight: 600, width: '45%', py: 0.5, px: 0.5, fontSize: '0.95rem' }}>
              File Name 
              </TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600, width: '30%', py: 0.5, px: 0.5, fontSize: '0.95rem' }}>
                Upload File
              </TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600, width: '10%', py: 0.5, px: 0.5, fontSize: '0.95rem' }}>
                View File
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {expectedFiles.map((fileName, index) => {
              const status = getFileStatus(fileName);
              const fileData = uploadedFiles[fileName];

              return (
                <TableRow key={index} sx={{ '&:hover': { backgroundColor: '#f8f8f8' } }}>
                  {/* Expected File Name */}
                  <TableCell sx={{ py: 0.25, px: 0.5, px: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.95rem' }}>
                      {fileName}
                    </Typography>
                  </TableCell>

                  {/* Upload Button */}
                  <TableCell sx={{ py: 1, px: 1 }}>
                    <Button
                      component="label"
                      variant="outlined"
                      startIcon={<CloudUpload sx={{ fontSize: '0.8rem' }} />}
                      size="small"
                      disabled={status === 'uploaded'}
                      sx={{
                        textTransform: 'none',
                        borderRadius: '0.4rem',
                        borderColor: '#034A7F',
                        color: '#034A7F',
                        fontSize: '0.78rem',
                        py: 0.6,
                        px: 1.6,
                        minHeight: '26px',
                        minWidth: '65px',
                        '&:hover': {
                          borderColor: '#034A7F',
                          backgroundColor: 'rgba(3, 74, 127, 0.04)',
                        },
                      }}
                    >
                      {status === 'uploaded' ? 'Uploaded' : 'Upload'}
                      <input
                        type="file"
                        hidden
                        accept=".pdf,.xlsx,.xls"
                        onChange={(e) => handleFileUpload(fileName, e)}
                      />
                    </Button>
                  </TableCell>

                  {/* View Icon */}
                  <TableCell sx={{ py: 0.25, px: 0.5 }}>
                    <IconButton
                      size="small"
                      disabled={status !== 'uploaded'}
                      onClick={() => handlePreview(fileName)}
                      sx={{ color: '#034A7F', p: 0.25 }}
                    >
                      <Visibility sx={{ fontSize: '1.1rem' }} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

        {/* Submit Button */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Button
          variant="outlined"
          onClick={() => navigate('/')}
          sx={{
            textTransform: 'none',
            borderRadius: '0.5rem',
            px: 4,
            borderColor: '#034A7F',
            color: '#034A7F',
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isUploading || Object.keys(uploadedFiles).length === 0}
          sx={{
            textTransform: 'none',
            borderRadius: '0.5rem',
            px: 4,
            backgroundColor: '#034A7F',
            '&:hover': {
              backgroundColor: '#023a66',
            },
          }}
        >
          {isUploading ? 'Submitting...' : 'Submit Application'}
        </Button>
      </Box>

      {/* Completion Progress Dialog */}
      <Dialog
        open={showCompletionUI}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            p: 2,
          },
        }}
      >
        <DialogContent>
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <CircularProgress
              variant="determinate"
              value={completionProgress}
              size={80}
              thickness={4}
              sx={{ color: '#034A7F', mb: 2 }}
            />
            <Typography variant="h6" sx={{ mb: 1, color: '#034A7F' }}>
              Processing Documents...
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {completionProgress}% Complete
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Success Dialog */}
      <Dialog
        open={showSuccessUI}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            p: 2,
          },
        }}
      >
        <DialogContent>
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <CheckCircle sx={{ fontSize: 80, color: '#5ABA85', mb: 2 }} />
            <Typography variant="h5" sx={{ mb: 1, color: '#034A7F', fontWeight: 600 }}>
              All Set!
            </Typography>
            <Typography variant="body1" color="text.secondary">
              All files have been uploaded successfully and new application has been created.
            </Typography>
            {uploadedAppId && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                Application ID: {uploadedAppId}
              </Typography>
            )}
            {uploadedBaseUrl && uploadedAppId && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                Files: <a href={`${uploadedBaseUrl}/${uploadedAppId}/Original_Docs/`} target="_blank" rel="noreferrer">View folder in Blob</a>
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 2 }}>
          <Button
            variant="contained"
            onClick={() => {
              setShowSuccessUI(false);
              setUploadedAppId(null);
              setUploadedBaseUrl(null);
              // Trigger HOME refresh and navigate back to Home immediately
              localStorage.setItem('HOME_REFRESH', Date.now().toString());
              navigate('/dashboard');
            }}
            fullWidth
            sx={{
              borderRadius: "0.5rem",
              textTransform: "none",
              px: 3,
              py: 0.75,
              fontSize: { xs: '0.813rem', sm: '0.875rem' },
              backgroundColor: "#034A7F",
              fontWeight: 600,
              maxWidth: { xs: '100%', sm: '250px' },
              "&:hover": { backgroundColor: "#034A7F" }
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* File Preview Dialog */}
      <Dialog
        open={openPreview}
        onClose={() => setOpenPreview(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            height: '80vh',
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle sx={{ backgroundColor: '#034A7F', color: 'white' }}>
          Preview: {previewFile?.name}
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {previewFile && (
            <iframe
              src={previewFile.url}
              style={{ width: '100%', height: '100%', border: 'none' }}
              title="File Preview"
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPreview(false)} sx={{ color: '#034A7F' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Role-Based Validation Error Dialog */}
      <Dialog
        open={showValidationDialog}
        onClose={() => setShowValidationDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: "0px 8px 24px rgba(0,0,0,0.2)",
          }
        }}
      >
        <DialogTitle
          sx={{
            fontSize: '1.1rem',
            fontWeight: 600,
            color: "#F11D28",
            py: 2,
            px: 3
          }}
        >
          {userRole} Validation Failed
        </DialogTitle>

        <DialogContent sx={{ pt: 1, px: 3 }}>
          <Typography variant="body2" sx={{ mb: 2, color: "#666" }}>
            {userRole === 'Super Admin' 
              ? 'Super Admin requires all 22 documents with exact file names. Please fix the following issues:'
              : 'Upload validation failed. Please fix the following issues:'
            }
          </Typography>

          {/* Validation Errors */}
          <Box sx={{ mb: 2 }}>
            {validationErrors.map((error, index) => (
              <Box key={index} sx={{ 
                p: 2, 
                mb: 1,
                backgroundColor: "#fff5f5", 
                borderRadius: "0.5rem",
                border: "1px solid #ffebee"
              }}>
                {error.type === 'missing_files' && (
                  <>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#F11D28", mb: 1 }}>
                      Missing Required Files ({error.missingFiles.length})
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#666", mb: 1 }}>
                      Expected: {error.totalRequired} files | Uploaded: {error.totalUploaded} files
                    </Typography>
                    <Box sx={{ maxHeight: 150, overflowY: 'auto' }}>
                      {error.missingFiles.map((file, idx) => (
                        <Typography key={idx} variant="body2" sx={{ color: "#F11D28", fontSize: '0.8rem' }}>
                          • {file}
                        </Typography>
                      ))}
                    </Box>
                  </>
                )}
                
                {error.type === 'incorrect_names' && (
                  <>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#F11D28", mb: 1 }}>
                      Incorrect File Names ({error.incorrectFiles.length})
                    </Typography>
                    <Box sx={{ maxHeight: 150, overflowY: 'auto' }}>
                      {error.incorrectFiles.map((file, idx) => (
                        <Typography key={idx} variant="body2" sx={{ color: "#F11D28", fontSize: '0.8rem' }}>
                          • {file} (not in required list)
                        </Typography>
                      ))}
                    </Box>
                  </>
                )}
                
                {error.type === 'count_mismatch' && (
                  <>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#F11D28", mb: 1 }}>
                      File Count Mismatch
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#666" }}>
                      Expected: {error.expected} files | Received: {error.received} files
                    </Typography>
                  </>
                )}
                
                {error.type === 'zip_error' && (
                  <>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#F11D28", mb: 1 }}>
                      ZIP File Error
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#666" }}>
                      {error.message}
                    </Typography>
                  </>
                )}
              </Box>
            ))}
          </Box>

          <Typography variant="body2" sx={{ color: "#666", fontStyle: 'italic' }}>
            Please ensure all required files are uploaded with exact file names as shown in the upload table.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => {
              setShowValidationDialog(false);
              setValidationErrors([]);
            }}
            variant="contained"
            sx={{
              borderRadius: "0.5rem",
              textTransform: "none",
              px: 3,
              py: 0.75,
              backgroundColor: "#034A7F",
              fontWeight: 600,
              "&:hover": {
                backgroundColor: "#023a66"
              }
            }}
          >
            I'll Fix the Files
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Toast Notifications */}
      <Toaster />
      </Box>
    </Box>
  );
};

export default UploadDocuments;